import axios from 'axios';
import {
  clearRenderedParticipants,
  renderRegistrationListIncremental,
} from './filter';
import { initMultiselect } from './multiselect.js';

let filters = {
  page: 1,
  sortMethod: 'desc',
  sortOrder: 'currentAmount',
  ngo: null,
  key: null,
};

let paginationState = {
  currentPage: 1,
  totalPages: 1,
};

function buildQueryString(params) {
  return Object.keys(params)
    .filter((key) => params[key] !== null && params[key] !== undefined)
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    .join('&');
}

function updateShowMoreButton(registrationsApiBase) {
  const showMoreBtn = document.querySelector('#show-more-registrations');
  if (!showMoreBtn) return;

  if (paginationState.currentPage >= paginationState.totalPages) {
    showMoreBtn.style.display = 'none';
  } else {
    showMoreBtn.style.display = 'block';
  }
}

function renderRegistrations(registrationsApiBase) {
  const queryString = buildQueryString(filters);
  renderRegistrationListIncremental(
    `${registrationsApiBase}?${queryString}`,
    '.registration-list__filter-list'
  ).then((response) => {
    if (response && response.totalPages !== undefined) {
      paginationState.totalPages = response.totalPages;
      paginationState.currentPage = response.currentPage || filters.page;
    }
    updateShowMoreButton(registrationsApiBase);
  });
}

export function setFilters(newFilters, registrationsApiBase) {
  filters = { ...filters, ...newFilters };
  filters.page = 1;
  paginationState.currentPage = 1;
  clearRenderedParticipants('.registration-list__filter-list');
  renderRegistrations(registrationsApiBase);
}

export function getFiltersNgoLength() {
  if (!filters.ngo) return null;
  return filters.ngo.split(',').filter((item) => item.trim() !== '').length;
}

class RegistrationList {
  constructor(element) {
    this.blockElement = element;

    this.apiUrls = {
      registrations: this.blockElement.getAttribute('data-registrations-api'),
      ngos: this.blockElement.getAttribute('data-ngo-api'),
    };

    this.apiUrls.promoted = this.apiUrls.registrations
      ? `${this.apiUrls.registrations}?promoted=1`
      : null;

    this.promoted = [];
    this.ngos = [];
  }

  init() {
    if (!this.apiUrls.registrations || !this.apiUrls.ngos) {
      console.error(
        'Brak atrybutów data-registrations-api lub data-ngo-api w bloku.',
        this.blockElement
      );
      return;
    }

    this.fetchData().then(() => {
      this.updateSum();
      this.updateNgosCount();
      this.updateRegistrationsCount();
      this.renderPromoted();

      if (this.ngos && this.ngos.length > 0) {
        initMultiselect(this.ngos, this.apiUrls.registrations);
      }

      renderRegistrations(this.apiUrls.registrations);
    });
  }

  async fetchData() {
    try {
      const [promotedRes, ngosRes] = await Promise.all([
        axios.get(this.apiUrls.promoted),
        axios.get(this.apiUrls.ngos),
      ]);

      this.promoted = promotedRes.data || [];
      this.ngos = ngosRes.data || [];
    } catch (error) {
      console.error(`Błąd podczas pobierania danych:`, error.message);
    }
  }

  updateSum() {
    if (this.promoted?.stats?.sum) {
      const sumElement = this.blockElement.querySelector(
        '.registration-list__sum'
      );
      if (sumElement) {
        sumElement.innerHTML = `${this.promoted.stats.sum.toLocaleString(
          'pl-PL'
        )} <span>zł</span>`;
      }
    }
  }

  updateNgosCount() {
    if (this.ngos) {
      const countElement = this.blockElement.querySelector(
        '.registration-list__ngo-count'
      );
      if (countElement) {
        countElement.innerHTML = this.ngos.length;
      }
    }
  }

  updateRegistrationsCount() {
    if (this.promoted?.stats?.total) {
      const sumElement = this.blockElement.querySelector(
        '.registration-list__registrations-count'
      );
      if (sumElement) {
        sumElement.innerHTML = this.promoted.stats.total;
      }
    }
  }

  renderPromoted() {
    const container = this.blockElement.querySelector(
      '.registration-list__promoted-registrations'
    );
    if (!container || !Array.isArray(this.promoted.entries)) return;

    container.innerHTML = '';

    this.promoted.entries.forEach((entry) => {
      const card = document.createElement('div');
      card.classList.add('registration-list__promoted');

      let progress = 12.5;
      let startPoint = 97;
      let startIsFinished = false;
      let targetIsFinished = false;

      if (entry.current) {
        let current = Number(entry.current);
        let pakietStart = Number(entry.pakiet_start);
        let target = Number(entry.target);

        startIsFinished = current >= pakietStart;
        targetIsFinished = target ? current >= target : false;

        if (target && target > pakietStart) {
          progress = ((current - pakietStart) / (target - pakietStart)) * 100;
        } else if (pakietStart > 0) {
          progress = ((current - pakietStart) / pakietStart) * 100;
        }

        progress = Math.min(Math.max(progress, 0), 100);
      }

      if (entry.target !== null && entry.target !== undefined) {
        const targetPoint = 97;
        startPoint = (entry.pakiet_start / entry.target) * 100;
      }

      const startingNrRaw = entry?.nr;
      const startingNr =
        startingNrRaw === null ||
        startingNrRaw === undefined ||
        String(startingNrRaw).trim() === '' ||
        String(startingNrRaw).trim() === '-'
          ? '-'
          : String(startingNrRaw).trim();

      card.innerHTML = `
    <div class="registration-list__promoted--image">
        <img src="${entry.img1}" alt="${entry.name}">
    </div>
    <div class="registration-list__promoted--text-content">
        <div class="registration-list__promoted--header">
        <div>
        <h4>${entry.name}</h4>
        <p class="is-style-tag">${window.sygnisoft_i18n.startingNr} <span>${
          startingNr
        }</span></p>
        </div>
        <div class="registration-list__promoted--logo-mobile">
            <img src="${entry.img2}" alt="Logo">
        </div>
        </div>
        <p>${truncateText(entry.desc, 243)}</p>
        <div class="promoted-table">
            <div class="promoted-table__title">
                <p class="promoted-table__title--current">${parseFloat(
                  entry.current
                ).toLocaleString('pl-PL')} zł</p>
                ${
                  entry.target
                    ? `<p class="promoted-table__title--target">z ${parseFloat(
                        entry.target
                      ).toLocaleString('pl-PL')} zł</p>`
                    : ''
                }
            </div>
            <div class="progress-bar">
                <span class="bar" style="width: ${progress}%"></span>
                <span class="point" style="left: ${startPoint}%"></span>
                <span class="point final-point"></span>
            </div>
            <p class="promoted-table__min-target ${
              startIsFinished ? 'finished' : ''
            }">
                ${window.sygnisoft_i18n.minAmount} ${entry.pakiet_start || '0'} zł
            </p>
            <p class="promoted-table__max-target ${
              targetIsFinished ? 'finished' : ''
            }">
                ${window.sygnisoft_i18n.maxAmount} ${
            entry.target ? entry.target + ' zł' : '-'
          }
            </p>
            <a class="wp-block-button__link wp-element-button" target="_blank" href="${
              entry.url || '#'
            }">
                ${window.sygnisoft_i18n.readMore}
            </a>
        </div>
    </div>
    <div class="registration-list__promoted--logo">
        <img src="${entry.img2}" alt="Logo">
    </div>
    `;
      container.appendChild(card);
    });
  }

  getRegistrationsApi() {
    return this.apiUrls.registrations;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.registration-list').forEach((blockElement) => {
    const instance = new RegistrationList(blockElement);
    instance.init();

    const showMoreBtn = blockElement.querySelector('#show-more-registrations');
    if (showMoreBtn) {
      showMoreBtn.addEventListener('click', () => {
        loadNextPage(instance.getRegistrationsApi());
      });
    }

    const amountBtn = blockElement.querySelector('#sort-registrations');
    if (amountBtn) {
      amountBtn.addEventListener('click', () => {
        setFilters(
          {
            sortMethod: filters.sortMethod === 'desc' ? 'asc' : 'desc',
          },
          instance.getRegistrationsApi()
        );
      });
    }

    const searchBtn = blockElement.querySelector('#search-key-registrations');
    const searchInputEl = blockElement.querySelector('.search-container input');

    if (searchBtn && searchInputEl) {
      const runSearch = () => {
        const searchInput = searchInputEl.value;
        setFilters(
          {
            key: searchInput,
          },
          instance.getRegistrationsApi()
        );
      };

      searchBtn.addEventListener('click', runSearch);

      searchInputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          runSearch();
        }
      });
    }
  });
});

function truncateText(text, maxLength) {
  if (!text) return '';
  if (text.length <= maxLength) return text;

  let truncated = text.substring(0, maxLength);
  truncated = truncated.substring(0, truncated.lastIndexOf(' '));
  return truncated + '...';
}

function loadNextPage(registrationsApiBase) {
  filters.page += 1;
  paginationState.currentPage += 1;
  return renderRegistrations(registrationsApiBase);
}
